// src/pages/MPFlow/phases/Phase3Refine.jsx
import React, { useState } from 'react';
import EdyMascot from '../components/EdyMascot';
import apiClient from '../../../lib/apiClient';
import CreativeCanvasEngine from '../../../engine/CreativeCanvasEngine/CreativeCanvasEngine';
import JobRequestModal from '../../../components/JobRequestModal';

const TOMS = [
  { key: 'engraçado',    label: 'Engraçado'    },
  { key: 'urgente',      label: 'Urgente'       },
  { key: 'profissional', label: 'Profissional'  },
];

const URL_REGEX = /^(https?:\/\/)?(wa\.me|api\.whatsapp\.com|www\.instagram\.com|instagram\.com|[a-zA-Z0-9-]+\.[a-zA-Z]{2,})(\/.*)?$/;

export default function Phase3Refine({ profile, creation, onNext, campaignId, onBack }) {
  const [copy, setCopy]       = useState(creation.copy);
  const [imageUrl, setImageUrl] = useState(creation.imageUrl || '');
  const [structure, setStructure] = useState(creation.structure);
  const [tom, setTom]         = useState(creation.tom || 'profissional');
  const [destino, setDestino] = useState('');
  const [destinoErr, setDestinoErr] = useState('');
  const [loadingTom, setLoadingTom]   = useState(false);
  const [loadingImg, setLoadingImg]   = useState(false);
  const [error, setError] = useState('');
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobDone, setJobDone]           = useState(null);

  // Muda tom → refina apenas a legenda (via /mp/refine-copy)
  const changeTom = async (newTom) => {
    if (newTom === tom) return;
    setLoadingTom(true);
    setError('');
    try {
      const res = await apiClient.request('/mp/refine-copy', {
        method: 'POST',
        body: JSON.stringify({
          headline:    copy?.headline || '',
          legenda:     copy?.legenda  || '',
          nicho:       profile.nicho,
          tom:         newTom,
          campaign_id: campaignId || '',
        }),
      });
      if (res?.legenda) {
        setCopy(prev => ({ ...prev, legenda: res.legenda }));
        setTom(newTom);
      }
    } catch {
      setError('Erro ao mudar tom. Tente novamente.');
    } finally {
      setLoadingTom(false);
    }
  };

  // Troca imagem → nova busca Pexels
  const swapImage = async () => {
    setLoadingImg(true);
    setError('');
    try {
      const res = await apiClient.request('/mp/image/search', {
        method: 'POST',
        body: JSON.stringify({ keyword: profile.nicho }),
      });
      if (res?.image_url) {
        setImageUrl(res.image_url);
        setStructure(null); // reset estrutura, vai exibir img direta
      }
    } catch {
      setError('Erro ao trocar imagem.');
    } finally {
      setLoadingImg(false);
    }
  };

  const validateDestino = (val) => {
    if (!val.trim()) { setDestinoErr(''); return true; } // opcional
    if (!URL_REGEX.test(val.trim())) {
      setDestinoErr('URL inválida. Use um link de WhatsApp ou Instagram.');
      return false;
    }
    setDestinoErr('');
    return true;
  };

  const handleNext = () => {
    if (destino && !validateDestino(destino)) return;
    onNext({ copy, imageUrl, structure, tom, destino });
  };

  return (
    <div>
      <EdyMascot phase="refino" />

      {error && <div className="mp-alert mp-alert--error">{error}</div>}

      <div className="mp-card">
        <h2 className="mp-phase-title">Refine seu Criativo</h2>
        <p className="mp-phase-sub">Ajuste o tom, a imagem e o destino da sua campanha.</p>

        {/* Preview */}
        {structure?.canvas_data ? (
          <div className="mp-creative-preview">
            <CreativeCanvasEngine structure={structure} scale={0.26} />
          </div>
        ) : structure?.image_url ? (
          <div className="mp-creative-preview">
            <img className="mp-preview-image" src={structure.image_url} alt="Criativo" />
          </div>
        ) : imageUrl ? (
          <div className="mp-creative-preview">
            <img className="mp-preview-image" src={imageUrl} alt="Criativo" />
          </div>
        ) : null}

        {/* Copy atual */}
        <div className="mp-copy-block">
          <div className="mp-copy-block__headline">
            {loadingTom ? 'Gerando...' : `"${copy?.headline || ''}"`}
          </div>
          <div className="mp-copy-block__legenda">
            {copy?.legenda || ''}
          </div>
        </div>

        {/* Preset de tom */}
        <p className="mp-field__label mp-mb-2">Mudar Tom:</p>
        <div className="mp-preset-row">
          {TOMS.map(t => (
            <button
              key={t.key}
              className={`mp-preset-btn ${tom === t.key ? 'mp-preset-btn--active' : ''}`}
              onClick={() => changeTom(t.key)}
              disabled={loadingTom}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Trocar imagem */}
        <button
          className="mp-btn mp-btn--ghost mp-btn--sm mp-my-3"
          onClick={swapImage}
          disabled={loadingImg}
        >
          {loadingImg ? 'Buscando...' : 'Trocar Imagem'}
        </button>

        {/* Solicitar Especialista */}
        <div className="mp-specialist-cta">
          {jobDone ? (
            <div className="mp-specialist-done">
              Especialista acionado! Job <code>{jobDone.job_id}</code> em andamento.
            </div>
          ) : (
            <button
              className="mp-btn mp-btn--specialist mp-btn--sm"
              onClick={() => setShowJobModal(true)}
            >
              Pedir Refino por Especialista - 3 creditos
            </button>
          )}
        </div>

        {/* Destino */}
        <div className="mp-field">
          <label className="mp-field__label">
            Link de Destino <span className="mp-text-muted">(opcional)</span>
          </label>
          <input
            className={`mp-field__input ${destinoErr ? 'mp-field__input--error' : ''}`}
            value={destino}
            onChange={e => { setDestino(e.target.value); validateDestino(e.target.value); }}
            placeholder="https://wa.me/5511... ou https://instagram.com/..."
          />
          {destinoErr && <span className="mp-field__error">{destinoErr}</span>}
        </div>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onBack && (
            <button className="mp-btn mp-btn--ghost" onClick={onBack}>
              ← Voltar
            </button>
          )}
          <button className="mp-btn mp-btn--primary" onClick={handleNext}>
            Próximo: Turbinar
          </button>
        </div>
      </div>

      {showJobModal && (
        <JobRequestModal
          type="refinement"
          campaignId={campaignId}
          onClose={() => setShowJobModal(false)}
          onSuccess={(d) => { setJobDone(d); setShowJobModal(false); }}
        />
      )}
    </div>
  );
}
